<script setup>
// Imports
import { format, useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'

// Services & stores
const $q = useQuasar()
const api = useFeathers()
const route = useRoute()

// Constants
const { t } = useI18n({ useScope: 'global' })
const { capitalize } = format
const itemsService = 'sharingcenter-appointments'
const translationKey = 'sharingcenter-appointments'
const allowed = new Set(['accepted', 'declined', 'tentative'])

// Refs
const hasError = ref(false)
const success = ref(false)
const resultText = ref('')
const chosenChoice = ref('')
const invitedParticipants = ref([])
const readyToSend = ref(null)
const isValidating = ref(false)
const rsvpNotifyOrganizer = ref(false)
const rsvpMessage = ref('')
const currentChoice = ref('')
const isOrganizer = ref(false)
const isAutoSubmitting = ref(false)
const showAllParticipants = ref(false)

// Computed properties
const canSubmit = computed(() => {
  const chosen = String(chosenChoice.value || '')
  const current = String(currentChoice.value || '')
  return readyToSend.value === true && allowed.has(chosen) && chosen !== current && !isValidating.value
})

const shouldShowParticipantsToggle = computed(() => invitedParticipants.value.length > 4)

const visibleParticipants = computed(() => {
  if (!shouldShowParticipantsToggle.value || showAllParticipants.value) {
    return invitedParticipants.value
  }
  return invitedParticipants.value.slice(0, 4)
})

const successIcon = computed(() => {
  if (chosenChoice.value === 'accepted') {
    return 'sym_o_check_circle'
  }
  if (chosenChoice.value === 'declined') {
    return 'sym_o_cancel'
  }
  if (chosenChoice.value === 'tentative') {
    return 'sym_o_help'
  }
  return 'sym_o_done_outline'
})

const successColor = computed(() => {
  if (chosenChoice.value === 'accepted') {
    return 'positive'
  }
  if (chosenChoice.value === 'declined') {
    return 'negative'
  }
  if (chosenChoice.value === 'tentative') {
    return 'warning'
  }
  return 'positive'
})

const sendLabel = computed(() => {
  if (chosenChoice.value === 'accepted') {
    return t('Customer.' + capitalize(translationKey) + '.Accept')
  }
  if (chosenChoice.value === 'declined') {
    return t('Customer.' + capitalize(translationKey) + '.Decline')
  }
  if (chosenChoice.value === 'tentative') {
    return t('Customer.' + capitalize(translationKey) + '.Maybe')
  }
  return t('Customer.' + capitalize(translationKey) + '.Send')
})

// Methods
const isSelected = (v) => {
  return chosenChoice.value === v
}

const isCurrentChoice = (v) => {
  return currentChoice.value === v
}

const participantStatusLabel = (status) => {
  const value = String(status || '').toLowerCase()
  if (value === 'accepted') {
    return t('Customer.' + capitalize(translationKey) + '.StatusOptions.Accepted')
  }
  if (value === 'declined') {
    return t('Customer.' + capitalize(translationKey) + '.StatusOptions.Declined')
  }
  if (value === 'tentative') {
    return t('Customer.' + capitalize(translationKey) + '.StatusOptions.Tentative')
  }
  return t('Customer.' + capitalize(translationKey) + '.StatusOptions.Waiting')
}

const participantStatusIcon = (status) => {
  const value = String(status || '').toLowerCase()
  if (value === 'accepted') {
    return 'sym_o_check_circle'
  }
  if (value === 'declined') {
    return 'sym_o_cancel'
  }
  if (value === 'tentative') {
    return 'sym_o_help'
  }
  return 'sym_o_schedule'
}

const participantStatusColor = (status) => {
  const value = String(status || '').toLowerCase()
  if (value === 'accepted') {
    return 'positive'
  }
  if (value === 'declined') {
    return 'negative'
  }
  if (value === 'tentative') {
    return 'warning'
  }
  return 'grey-7'
}

const toggleParticipants = () => {
  showAllParticipants.value = !showAllParticipants.value
}

const normalizeParticipants = (payload) => {
  const direct = Array.isArray(payload?.participants) ? payload.participants : null
  const nested = Array.isArray(payload?.data?.participants) ? payload.data.participants : null
  const source = direct || nested || []

  return source.map((p) => {
    const firstName = String(p?.firstName || '').trim()
    const lastName = String(p?.lastName || '').trim()
    const fullName = `${firstName} ${lastName}`.trim()
    const email = String(p?.email || '').trim()
    const phone = String(p?.phone || '').trim()
    return {
      id: String(p?._id || p?.uniqueIdentifier || p?.user || p?.token || `${fullName}-${email}-${phone}`),
      name: fullName || '',
      email: email || '',
      phone: phone || '',
      label: fullName || email || phone || '-',
      status: String(p?.responseStatus || ''),
    }
  })
}

const validate = async (choice) => {
  const id = String(route.query.id || '')
  const token = String(route.query.token || '')

  isValidating.value = true
  hasError.value = false
  success.value = false

  try {
    const payload = { id, token }
    if (allowed.has(String(choice || ''))) {
      payload.choice = String(choice)
    }

    const result = await api.service(itemsService).validateRSVP(payload)
    invitedParticipants.value = normalizeParticipants(result)
    const status = String(result?.participant?.currentStatus || '')
    currentChoice.value = allowed.has(status) ? status : ''
    isOrganizer.value = result?.participant?.isOrganizer === true
    if (isOrganizer.value) {
      rsvpNotifyOrganizer.value = false
      rsvpMessage.value = ''
    }
    readyToSend.value = true
    return result
  } catch {
    invitedParticipants.value = []
    currentChoice.value = ''
    isOrganizer.value = false
    hasError.value = true
    readyToSend.value = false
    return null
  } finally {
    isValidating.value = false
  }
}

const setChoice = async (choice) => {
  if (!allowed.has(choice)) {
    return
  }
  if (isValidating.value) {
    return
  }
  if (chosenChoice.value === choice) {
    return
  }
  if (isOrganizer.value && currentChoice.value === choice) {
    return
  }
  chosenChoice.value = choice
  await validate(choice)
  if (isOrganizer.value && readyToSend.value === true) {
    await submitRSVP()
  }
}

watch(
  () => rsvpNotifyOrganizer.value,
  (enabled) => {
    if (!enabled) {
      rsvpMessage.value = ''
    }
  },
)

const submitRSVP = async () => {
  const id = String(route.query.id || '')
  const token = String(route.query.token || '')
  const choice = String(chosenChoice.value || '')
  if (!allowed.has(choice)) {
    return
  }

  try {
    const shouldNotifyOrganizer = !isOrganizer.value && rsvpNotifyOrganizer.value === true
    const organizerMessage = shouldNotifyOrganizer ? String(rsvpMessage.value || '') : ''

    await api.service(itemsService).setRSVP({
      id,
      token,
      choice,
      message: organizerMessage,
      notifyOrganizer: shouldNotifyOrganizer,
    })

    if (choice === 'accepted') {
      resultText.value = t('Customer.' + capitalize(translationKey) + '.RsvpAccepted')
    } else if (choice === 'declined') {
      resultText.value = t('Customer.' + capitalize(translationKey) + '.RsvpDeclined')
    } else if (choice === 'tentative') {
      resultText.value = t('Customer.' + capitalize(translationKey) + '.RsvpTentative')
    } else {
      resultText.value = t('Customer.' + capitalize(translationKey) + '.RsvpGeneric')
    }

    const notifyIcon = choice === 'accepted' ? 'sym_o_check_circle' : choice === 'declined' ? 'sym_o_cancel' : 'sym_o_help'
    $q.notify({ type: 'positive', message: resultText.value, icon: notifyIcon, progress: true })
    currentChoice.value = choice
    success.value = true
  } catch {
    hasError.value = true
    $q.notify({ type: 'negative', message: t('Customer.' + capitalize(translationKey) + '.Rsvp failed'), icon: 'sym_o_error_outline', progress: true })
  } finally {
    isAutoSubmitting.value = false
  }
}

// Lifecycle
onBeforeMount(async () => {
  const id = String(route.query.id || '')
  const token = String(route.query.token || '')
  const choice = String(route.query.choice || '')
  const hasChoiceInUrl = allowed.has(choice)

  chosenChoice.value = hasChoiceInUrl ? choice : ''

  if (!id || !token) {
    hasError.value = true
    readyToSend.value = false
    return
  }

  if (hasChoiceInUrl) {
    isAutoSubmitting.value = true
  }

  const validationResult = await validate(chosenChoice.value)
  if (hasChoiceInUrl && isOrganizer.value && allowed.has(chosenChoice.value) && chosenChoice.value !== currentChoice.value) {
    await submitRSVP()
    return
  }
  isAutoSubmitting.value = false
  if (!hasChoiceInUrl) {
    const currentStatus = String(validationResult?.participant?.currentStatus || '')
    if (allowed.has(currentStatus)) {
      chosenChoice.value = currentStatus
    }
  }
})
</script>

<template>
  <div
    class="flex column flex-center text-center q-px-sm-lg q-pb-sm-md"
    :class="hasError || success || isAutoSubmitting ? 'min-height-px-200' : 'min-height-px-300'"
  >
    <template v-if="readyToSend === null">
      <q-spinner size="2rem" color="primary" />
    </template>

    <template v-else-if="readyToSend === false && !isValidating && !success">
      <q-avatar size="6rem" class="q-mb-sm">
        <q-icon name="sym_o_error_outline" color="negative" size="4rem" />
      </q-avatar>
      <span>{{ $t('Customer.' + capitalize(translationKey) + '.InvalidLink') }}</span>
    </template>

    <template v-else-if="success">
      <q-avatar size="6rem" class="q-mb-sm">
        <q-icon :name="successIcon" :color="successColor" size="4rem" />
      </q-avatar>
      <span>{{ resultText }}</span>
    </template>

    <template v-else-if="isAutoSubmitting">
      <q-spinner size="2rem" color="primary" />
    </template>

    <template v-else-if="readyToSend === true || isValidating">
      <div class="full-width">
        <div class="row q-col-gutter-sm q-mb-lg">
          <div class="col-12 col-sm-4">
            <q-btn
              class="full-width"
              no-caps
              :outline="!isSelected('accepted')"
              :unelevated="isSelected('accepted')"
              :color="isSelected('accepted') ? 'positive' : 'grey-5'"
              :text-color="isSelected('accepted') ? 'white' : 'grey-8'"
              :disable="(isOrganizer && isCurrentChoice('accepted')) || isValidating"
              :align="$q.screen.lt.sm ? 'left' : 'center'"
              @click="setChoice('accepted')"
            >
              <q-icon name="sym_o_check_circle" :color="isSelected('accepted') ? 'white' : 'positive'" class="q-mr-sm" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Accept') }}
            </q-btn>
          </div>

          <div class="col-12 col-sm-4">
            <q-btn
              class="full-width"
              no-caps
              :outline="!isSelected('tentative')"
              :unelevated="isSelected('tentative')"
              :color="isSelected('tentative') ? 'warning' : 'grey-5'"
              :text-color="isSelected('tentative') ? 'white' : 'grey-8'"
              :disable="(isOrganizer && isCurrentChoice('tentative')) || isValidating"
              :align="$q.screen.lt.sm ? 'left' : 'center'"
              @click="setChoice('tentative')"
            >
              <q-icon name="sym_o_help" :color="isSelected('tentative') ? 'white' : 'warning'" class="q-mr-sm" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Maybe') }}
            </q-btn>
          </div>

          <div class="col-12 col-sm-4">
            <q-btn
              class="full-width"
              no-caps
              :outline="!isSelected('declined')"
              :unelevated="isSelected('declined')"
              :color="isSelected('declined') ? 'negative' : 'grey-5'"
              :text-color="isSelected('declined') ? 'white' : 'grey-8'"
              :disable="(isOrganizer && isCurrentChoice('declined')) || isValidating"
              :align="$q.screen.lt.sm ? 'left' : 'center'"
              @click="setChoice('declined')"
            >
              <q-icon name="sym_o_cancel" :color="isSelected('declined') ? 'white' : 'negative'" class="q-mr-sm" />
              {{ $t('Customer.' + capitalize(translationKey) + '.Decline') }}
            </q-btn>
          </div>
        </div>

        <div v-if="invitedParticipants.length > 0" class="text-left q-mb-lg">
          <div class="text-weight-medium q-mb-xs">
            {{ $t('Customer.' + capitalize(translationKey) + '.Participants') }}
          </div>
          <q-list bordered separator class="rounded-borders" style="overflow: hidden">
            <q-item v-for="participant in visibleParticipants" :key="participant.id" class="q-pt-sm q-pb-sm">
              <q-item-section>
                <q-item-label>{{ participant.name || participant.email || participant.phone || '-' }}</q-item-label>
                <q-item-label v-if="participant.email && participant.name" class="text-body2 text-grey-8">{{ participant.email }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <div class="row items-center no-wrap q-gutter-xs">
                  <q-icon
                    v-if="participant.status"
                    :name="participantStatusIcon(participant.status)"
                    :color="participantStatusColor(participant.status)"
                    size="24px"
                  >
                    <q-tooltip>{{ participantStatusLabel(participant.status) }}</q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-if="shouldShowParticipantsToggle" class="q-mt-xs">
            <q-btn
              flat
              dense
              no-caps
              color="primary"
              :icon="showAllParticipants ? 'sym_o_keyboard_arrow_up' : 'sym_o_keyboard_arrow_down'"
              :label="
                showAllParticipants ? $t('Customer.' + capitalize(translationKey) + '.Show less') : $t('Customer.' + capitalize(translationKey) + '.Show more')
              "
              @click="toggleParticipants"
            />
          </div>
        </div>

        <template v-if="!isOrganizer">
          <div
            class="full-width rounded-borders q-px-sm q-py-sm text-left q-mb-sm cursor-pointer"
            style="border: 1px solid rgba(0, 0, 0, 0.15)"
            @click="rsvpNotifyOrganizer = !rsvpNotifyOrganizer"
          >
            <q-toggle v-model="rsvpNotifyOrganizer" :label="$t('Customer.' + capitalize(translationKey) + '.Email organizer')" dense @click.stop />
          </div>

          <q-input
            v-model="rsvpMessage"
            type="textarea"
            outlined
            bg-color="grey-1"
            :rows="5"
            :readonly="!rsvpNotifyOrganizer"
            :label="$t('Customer.' + capitalize(translationKey) + '.Message optional')"
            class="rsvp-input q-mb-md q-field--error"
          />

          <q-btn color="primary" :loading="isValidating" :disable="!canSubmit" :label="sendLabel" @click="submitRSVP" />
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped>
:deep(.q-btn--outline:before) {
  border-color: rgba(0, 0, 0, 0.15) !important;
}
</style>
